import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import IconButton from '@atlaskit/button/icon/button';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CopyIcon from '@atlaskit/icon/core/copy';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useIntl, defineMessage } from '@jira/platform__react-intl';

const copyTooltipHover = defineMessage({
	defaultMessage: 'Copy {itemToCopy}',
	id: 'applinks.administration.ui.common.fields.copy-button.copy-tooltip-hover',
	description: 'Tooltip shown on hover, informs user that clicking will copy content',
});

const copyTooltipFeedback = defineMessage({
	defaultMessage: 'Copied',
	id: 'applinks.administration.ui.common.fields.copy-button.copy-tooltip-feedback',
	description: 'Feedback message shown in tooltip after content is copied',
});

const TOOLTIP_CONTENT_REFRESH_INTERVAL = 2500;

const CopyButton = (
	props: { label: string; value: string } & React.RefAttributes<unknown>,
): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const [linkCopied, setLinkCopied] = useState(false);

	const [message, setMessage] = useState(
		formatMessage(copyTooltipHover, { itemToCopy: props.label }),
	);
	const updateTooltip = useRef<() => void>();

	useLayoutEffect(() => {
		updateTooltip.current?.();
		// oxlint-disable-next-line react/exhaustive-effect-dependencies
	}, [message]);

	const copyLink = useCallback(async () => {
		if (typeof globalThis !== 'undefined' && globalThis.navigator?.clipboard) {
			try {
				await globalThis.navigator.clipboard.writeText(props.value);
				setLinkCopied(true);
				setMessage(formatMessage(copyTooltipFeedback));
				setTimeout(() => {
					setLinkCopied(false);
					setMessage(formatMessage(copyTooltipHover, { itemToCopy: props.label }));
				}, TOOLTIP_CONTENT_REFRESH_INTERVAL);
			} catch (error) {
				// TODO: Handle error case where clipboard write fails
			}
		}
	}, [formatMessage, props.label, props.value]);

	return (
		<Tooltip
			content={({ update }) => {
				updateTooltip.current = update;
				return message;
			}}
			position="bottom"
		>
			<IconButton
				appearance="subtle"
				icon={linkCopied ? CheckCircleIcon : CopyIcon}
				label={formatMessage(copyTooltipHover, { itemToCopy: props.label })}
				isTooltipDisabled
				onClick={copyLink}
			/>
		</Tooltip>
	);
};

export default CopyButton;
